import { dayText, gallons, galText } from "./accounts";
import type { Assignee, DuplicateItem, DuplicatePickup, DuplicateWeek, OldEntry, Review, ReviewItem, ReviewRecord } from "./api";
import { localNow, REASON_TEXT } from "./visits";

export const NOTE_MAX = 1000;

export const RATE_MAX = 10_000;

export const REVIEW_KINDS = ["overflow", "small_container", "rate_high", "rate_low", "high_scrap"] as const;
export type ReviewKind = (typeof REVIEW_KINDS)[number];

export const RESULTS = ["resize", "rate", "week_group", "sales", "no_change"] as const;
export type ReviewResult = (typeof RESULTS)[number];

export const KIND_TEXT: Record<ReviewKind, string> = {
  overflow: "Overflow calls",
  small_container: "Small containers",
  rate_high: "Rate above the stated rate",
  rate_low: "Rate below the stated rate",
  high_scrap: "High scrap",
};

/** The kind of one item, in a table cell. */
export const KIND_SHORT: Record<ReviewKind, string> = {
  overflow: "Overflow",
  small_container: "Small container",
  rate_high: "Rate high",
  rate_low: "Rate low",
  high_scrap: "High scrap",
};

export const RESULT_TEXT: Record<ReviewResult, string> = {
  resize: "Change the container size",
  rate: "Change the stated rate",
  week_group: "Change the week group",
  sales: "Send to sales",
  no_change: "No change",
};

/** The text of the button that saves each result. */
export const SAVE_TEXT: Record<ReviewResult, string> = {
  resize: "Open the resize order",
  rate: "Save the stated rate",
  week_group: "Save the week group",
  sales: "Open the sales issue",
  no_change: "Record no change",
};

export interface DecisionForm {
  note: string;
  rate: string;
  weeks: string;
  pull: number | null;
  drop: number | null;
  assignee: string;
}

export type DecisionField = "note" | "rate" | "weeks" | "drop" | "assignee";

/** The kinds that ask for more room: a size at or below the pulled one is never the preset. */
export const GROWS: ReadonlySet<ReviewKind> = new Set(["overflow", "small_container", "rate_high"]);

type SizedItem = Pick<ReviewItem, "kind" | "recommended_sizes" | "sizes">;

/** The container that a resize pulls: the chosen one, else the largest on the account. */
export function pulledSize(item: Pick<ReviewItem, "sizes">, pull: number | null): number | null {
  return pull ?? (item.sizes.length ? Math.max(...item.sizes) : null);
}

/** The size to drop by default, as the server presets it, or null. */
export function defaultDrop(item: SizedItem, pull: number | null = null): number | null {
  const sizes = item.recommended_sizes ?? [];
  if (sizes.length !== 1) return null;
  const pulled = pulledSize(item, pull);
  if (GROWS.has(item.kind) && pulled !== null && sizes[0] <= pulled) return null;
  return sizes[0];
}

/** Why no size is preset on an item that asks for more room, or null. */
export function noPresetText(item: SizedItem, pull: number | null = null): string | null {
  const sizes = item.recommended_sizes ?? [];
  const pulled = pulledSize(item, pull);
  if (!GROWS.has(item.kind) || sizes.length !== 1 || pulled === null || sizes[0] > pulled) return null;
  return `The recommendation is not larger than the current container (${galText(pulled)}). Choose the size.`;
}

export function rateText(value: number): string {
  return `${value.toFixed(1)} gal a week`;
}

/** The visit frequency of a cycle length in weeks. */
export function visitsText(weeks: number | null | undefined): string {
  if (!weeks) return "No visits";
  return weeks === 1 ? "every week" : `every ${weeks} weeks`;
}

/** "35 gal (down from 180 gal), for 24.3 gal between visits, every week". */
export function reviewRecommendedText(
  item: Pick<ReviewItem, "need_gal" | "recommended_sizes" | "recommended_gal" | "capacity_gal" | "cycle_weeks">,
): string {
  if (item.need_gal == null) return "No estimate";
  const need = `for ${item.need_gal.toFixed(1)} gal between visits, ${visitsText(item.cycle_weeks)}`;
  const sizes = item.recommended_sizes ?? [];
  if (sizes.length === 0 || item.recommended_gal == null) return `No standard size is large enough ${need}`;
  const size = sizes.map(galText).join(" + ");
  const now = item.capacity_gal;
  const change =
    item.recommended_gal === now ? "same as now" : `${item.recommended_gal < now ? "down" : "up"} from ${galText(now)}`;
  return `${size} (${change}), ${need}`;
}

export const NO_LARGER_TEXT = "No larger size";

/** The Recommended cell: a short text, and the full estimate for its title. It uses the preset rule of the form. */
export function reviewRecommendedCell(
  item: Pick<
    ReviewItem,
    "kind" | "sizes" | "need_gal" | "recommended_sizes" | "recommended_gal" | "capacity_gal" | "cycle_weeks"
  >,
): { text: string; title: string } {
  const title = `Estimate: ${reviewRecommendedText(item)}`;
  const sizes = item.recommended_sizes ?? [];
  if (item.need_gal == null) return { text: "No estimate", title };
  if (sizes.length === 0 || item.recommended_gal == null) return { text: "None large enough", title };
  const pulled = pulledSize(item, null);
  if (GROWS.has(item.kind) && pulled !== null && item.recommended_gal <= pulled)
    return { text: NO_LARGER_TEXT, title: `No larger size needed at the measured rate. ${title}` };
  const size = sizes.map(galText).join(" + ");
  const now = item.capacity_gal;
  return { text: item.recommended_gal === now ? `${size} (same)` : `${size} (was ${gallons(now)})`, title };
}

/** The Containers cell: "180 gal · every 4 wk", and the full text for its title. */
export function containersCell(item: Pick<ReviewItem, "capacity_gal" | "cap_weeks" | "cycle_weeks">): { text: string; title: string } {
  const weeks = item.cycle_weeks;
  const visits = !weeks ? "no visits" : weeks === 1 ? "weekly" : `every ${weeks} wk`;
  return { text: `${galText(item.capacity_gal)} · ${visits}`, title: `${containersText(item)}; ${weeks ? `visits ${visitsText(weeks)}` : "no visits"}` };
}

/** "180 gal, 9.0 weeks at the stated rate". */
export function containersText(item: Pick<ReviewItem, "capacity_gal" | "cap_weeks">): string {
  const weeks = item.cap_weeks === null ? "" : `, ${item.cap_weeks.toFixed(1)} weeks at the stated rate`;
  return `${galText(item.capacity_gal)}${weeks}`;
}

const SALES_ROLES = ["sales_manager", "salesperson"];

/** The members for a sales issue: the sales roles first, then the others, each in email order. */
export function salesFirst(people: readonly Assignee[]): Assignee[] {
  const sells = (person: Assignee) => (person.roles ?? []).some((role) => SALES_ROLES.includes(role));
  return [...people.filter(sells), ...people.filter((person) => !sells(person))];
}

export function assigneeText(person: Assignee): string {
  const roles = (person.roles ?? []).map((role) => role.replaceAll("_", " "));
  return roles.length ? `${person.email} (${roles.join(", ")})` : person.email;
}

/** Check the form of one result; give the field and the error text, or null. */
export function decisionError(
  result: ReviewResult,
  form: DecisionForm,
  item: Pick<ReviewItem, "stated_rate" | "week_group" | "sizes">,
): { field: DecisionField; text: string } | null {
  const note = form.note.trim();
  if (note.length > NOTE_MAX) return { field: "note", text: `The note can have ${NOTE_MAX} characters at most.` };
  if (result === "no_change" && !note) return { field: "note", text: "Give the reason for no change." };
  if (result === "rate") {
    const rate = Number(form.rate.trim());
    if (!form.rate.trim() || !Number.isFinite(rate) || rate < 0 || rate > RATE_MAX) {
      return { field: "rate", text: `Give the stated rate in gal a week, from 0 to ${RATE_MAX}.` };
    }
    if (Math.round(rate * 100) === Math.round(item.stated_rate * 100)) {
      return { field: "rate", text: "Give a rate that is different from the stated rate." };
    }
  }
  if (result === "week_group") {
    const weeks = form.weeks.trim();
    const parts = weeks.split("-");
    if (!/^[1-4](-[1-4]){0,3}$/.test(weeks) || parts.join() !== [...new Set(parts)].sort().join()) {
      return { field: "weeks", text: "List each week once and in order, for example 1-3." };
    }
    if (weeks === item.week_group) return { field: "weeks", text: "Give a week group that is different from the plan." };
  }
  if (result === "resize") {
    if (form.drop === null) return { field: "drop", text: "Choose the size of the new container." };
    if (pulledSize(item, form.pull) === form.drop) return { field: "drop", text: "Choose a new size for the resize." };
  }
  if (result === "sales" && !form.assignee) return { field: "assignee", text: "Choose who takes the sales issue." };
  return null;
}

/** The body of the decision, with only the field that the result uses. */
export function decisionBody(id: string, kind: ReviewKind, result: ReviewResult, form: DecisionForm) {
  const base = { id, kind, result, note: form.note.trim() };
  if (result === "rate") return { ...base, stated_rate: Number(form.rate.trim()) };
  if (result === "week_group") return { ...base, week_group: form.weeks.trim() };
  if (result === "resize") return { ...base, to_size: form.drop ?? undefined, from_size: form.pull ?? undefined };
  if (result === "sales") return { ...base, assignee_id: form.assignee };
  return base;
}

export function countText(count: number): string {
  return count === 1 ? "1 account" : `${count} accounts`;
}

/** The weekly averages, the newest period first: "12.0, 11.5, 0.0, 0.0 gal a week". */
export function averagesText(averages: readonly number[]): string {
  return `${averages.map((value) => value.toFixed(1)).join(", ")} gal a week`;
}

/** "Reviewed 2026-10-12: no change, until 2026-11-01" for the newest decision on a kind that still holds. */
export function reviewedText(kind: ReviewKind, records: readonly ReviewRecord[], today: string): string | null {
  const found = records.find((record) => record.kind === kind);
  if (!found || (found.until !== null && found.until < today)) return null;
  const day = (found.decided_at ?? "").slice(0, 10);
  const until = found.until ? `, until ${found.until}` : "";
  return `Reviewed ${day}: ${RESULT_TEXT[found.result].toLowerCase()}${until}`;
}

/** The most weeks that one "Both are right" request holds. */
export const ACK_MAX = 10_000;

export const duplicateKey = (item: Pick<DuplicateItem, "account_id" | "week">) => `${item.account_id}/${item.week}`;

const SOURCE_TEXT: Record<DuplicatePickup["source"], string> = { screen: "app", seed: "seed", import: "sheet" };

/** "Oct 6: 42/9 (app)". */
export function duplicatePickupText(pickup: DuplicatePickup): string {
  return `${dayText(pickup.at)}: ${gallons(pickup.raw_gal)}/${gallons(pickup.scrap_gal)} (${SOURCE_TEXT[pickup.source]})`;
}

/** "Oct 5 to Oct 11". */
export const weekText = (item: Pick<DuplicateItem, "week" | "week_end">) => `${dayText(item.week)} to ${dayText(item.week_end)}`;

/** "Oct 5–11", or "Sep 28–Oct 4" across two months, for a narrow column. */
export function weekShort(item: Pick<DuplicateItem, "week" | "week_end">): string {
  const sameMonth = item.week.slice(0, 7) === item.week_end.slice(0, 7);
  return `${dayText(item.week)}–${sameMonth ? Number(item.week_end.slice(8)) : dayText(item.week_end)}`;
}

/** A visit is corrected on the Visits page of its day, other pickups on the account page. */
export function correctHref(base: string, item: Pick<DuplicateItem, "account_id">, pickup: DuplicatePickup): string {
  return pickup.visit_id ? `${base}/visits?day=${pickup.at}` : `${base}/accounts/${item.account_id}`;
}

export const ackWeek = (item: DuplicateItem): DuplicateWeek => ({
  account_id: item.account_id,
  week: item.week,
  pickup_ids: item.pickups.map((pickup) => pickup.id),
});

/** The weeks in requests of ACK_MAX at most, so a selection of any size is sent. */
export function ackChunks(items: readonly DuplicateItem[]): DuplicateWeek[][] {
  const chunks: DuplicateWeek[][] = [];
  for (let start = 0; start < items.length; start += ACK_MAX) chunks.push(items.slice(start, start + ACK_MAX).map(ackWeek));
  return chunks;
}

/** The review without the weeks that got an answer. */
export function withoutWeeks(review: Review, answered: ReadonlySet<string>): Review {
  return { ...review, duplicates: (review.duplicates ?? []).filter((item) => !answered.has(duplicateKey(item))) };
}

/** The pickups that a week shows before "Show all". */
export const DUPLICATE_FIRST = 3;

export const shownPickups = (item: Pick<DuplicateItem, "pickups">, all: boolean) =>
  all ? item.pickups : item.pickups.slice(0, DUPLICATE_FIRST);

/** "Both are right" for weeks of 2 pickups, else "All are right". */
export const ackText = (items: readonly Pick<DuplicateItem, "pickups">[]) =>
  items.every((item) => item.pickups.length === 2) ? "Both are right" : "All are right";

/** "both pickups", "all 4 pickups", or "all pickups" for weeks of different sizes. */
export function ackedText(items: readonly Pick<DuplicateItem, "pickups">[]): string {
  if (ackText(items) === "Both are right") return "both pickups";
  return items.length === 1 ? `all ${items[0].pickups.length.toLocaleString("en-US")} pickups` : "all pickups";
}

export function weeksText(count: number): string {
  return count === 1 ? "1 week" : `${count.toLocaleString("en-US")} weeks`;
}

/** The visit of an old entry in a few words: "Pickup 30/2 gal" or "Not full, 40 gal left". */
export function oldEntryWhat(item: Pick<OldEntry, "kind" | "raw_gal" | "scrap_gal" | "gallons_left" | "reason">): string {
  if (item.kind === "pickup") return `Pickup ${gallons(item.raw_gal ?? 0)}/${galText(item.scrap_gal ?? 0)}`;
  const reason = REASON_TEXT[item.reason ?? (item.kind === "level" ? "not_full" : "no_access")];
  return item.gallons_left === null ? reason : `${reason}, ${galText(item.gallons_left)} left`;
}

/** The day and time, in the time zone of the organization; the year shows only when it is not the year of `now`. */
export function oldEntryWhen(at: string, timeZone: string, now: Date = new Date()): { day: string; text: string } {
  const { day, time } = localNow(new Date(at), timeZone);
  return { day, text: `${dayText(day, day.slice(0, 4) !== localNow(now, timeZone).day.slice(0, 4))} ${time}` };
}

/** The visit ids in requests of ACK_MAX at most. */
export function oldEntryChunks(ids: readonly string[]): string[][] {
  const chunks: string[][] = [];
  for (let start = 0; start < ids.length; start += ACK_MAX) chunks.push(ids.slice(start, start + ACK_MAX));
  return chunks;
}

export function withoutOldEntries(review: Review, checked: ReadonlySet<string>): Review {
  return { ...review, old_entries: (review.old_entries ?? []).filter((item) => !checked.has(item.visit_id)) };
}

export const oldEntriesText = (n: number) => (n === 1 ? "1 entry" : `${n.toLocaleString("en-US")} entries`);

/** The line at the top of Review that links to the list at the end. */
export const awayText = (n: number) => `${oldEntriesText(n)} saved away from the stop (last 7 days)`;
